import ExcelJS from 'exceljs';
import { CargoEventReportData } from '../../ReportGeneratorTypes';
import { setCellValueWithFont, setupWorksheetForPrint, setWorksheetPrintArea } from './reportEventExcelUtils';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import {
    addChartererAndDateTime,
    addOdfjellHeader,
    addVesselDetails,
    getCargoName,
    getCargoNumber,
    getCargoOperations,
    odfjellHeaderColor,
    replaceLoadDischargePhrase,
} from './parts/helpers';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { formatDateTime, prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { EventInputEnum } from 'src/enums/eventInputEnum';
import { getNominatedWeighAfterOptionAdjustement } from 'src/utils/stowage/cargoOptionUtils';

const addReportHeader = (worksheet: ExcelJS.Worksheet, logoImageId: number, cargoOperation: RotationCargoOperation): void => {
    let rowNum = addOdfjellHeader(worksheet, logoImageId);
    rowNum = 6;
    setCellValueWithFont(worksheet, rowNum, 1, `NOTICE OF READINESS  -  ${replaceLoadDischargePhrase(cargoOperation, true, 'ing')}`, {
        bold: true,
        color: odfjellHeaderColor,
        fontSize: 16,
        mergeAcross: 9,
    });
};

const addPortDescription = (
    worksheet: ExcelJS.Worksheet,
    selectedEvent: TankersEvent,
    portCall: RotationPortCall,
    cargoOperations: RotationCargoOperation[]
): void => {
    let rowNum = 13;
    setCellValueWithFont(worksheet, rowNum, 1, 'Dear Sir or Madam,', { bold: true, mergeAcross: 3, align: 'left' });
    rowNum += 2;

    const time = formatDateTime(selectedEvent.eventDate.actualDateTimeLocal, 'HH:mm');
    setCellValueWithFont(
        worksheet,
        rowNum,
        1,
        `Please be advised of the arrival of the above named vessel in the Port of ${portCall.portDisplayName} at \n${time} hrs today.`,
        { mergeAcross: 9, mergeDown: 2, wrapText: true, align: 'left' }
    );

    rowNum += 3;
    const cpDate = cargoOperations.find((co) => co.cpDate)?.cpDate;
    setCellValueWithFont(
        worksheet,
        rowNum,
        1,
        `The vessel is in every respect, and in accordance with C/P dated ${
            cpDate ? prettifyDate(cpDate) : 'N/A'
        }, ready to\ncommence ${replaceLoadDischargePhrase(cargoOperations[0], false, 'ing')} of the following quantities:`,
        { mergeAcross: 9, mergeDown: 2, wrapText: true, align: 'left' }
    );
};

const addCargoOperationRows = (worksheet: ExcelJS.Worksheet, cargoOperations: RotationCargoOperation[]): number => {
    let rowNumber = 21;

    // Headers
    [
        { col: 3, text: 'Cgo No.' },
        { col: 4, text: 'Commodity' },
        { col: 8, text: 'Quantity', style: { mergeAcross: 2 as number } },
    ].forEach((h) => setCellValueWithFont(worksheet, rowNumber, h.col, h.text, { bold: true, underline: true, ...(h.style || {}) }));

    const borderColor = '#D9D9D9';
    rowNumber += 2;

    const lastIndex = cargoOperations.length - 1;

    // Column definitions for body rows
    const cols = [
        { col: 3, val: (co: RotationCargoOperation) => getCargoNumber(co), right: true },
        { col: 4, val: (co: RotationCargoOperation) => getCargoName(co) },
        {
            col: 8,
            val: (co: RotationCargoOperation) => {
                // Show BL figure if available; otherwise nominated (option adjusted); fallback to contract weight
                const bl = co.billOfLadingWeight;
                if (typeof bl === 'number' && bl > 0) return bl;
                if (typeof co.nominatedWeight === 'number' && co.nominatedWeight > 0) return co.nominatedWeight;
                const computedNom = getNominatedWeighAfterOptionAdjustement(co.contractWeight, co.cargo);
                if (typeof computedNom === 'number' && computedNom > 0) return computedNom;
                return co.contractWeight;
            },
            right: true,
        },
        { col: 9, val: () => 'Mt' },
    ] as const;

    cargoOperations.forEach((co, index) => {
        const isLast = index === lastIndex;
        cols.forEach((cfg) => {
            const border = {
                ...(isLast ? {} : { bottom: { color: borderColor } }),
                ...('right' in cfg && (cfg as any).right ? { right: { color: borderColor } } : {}),
            } as any;
            const style = Object.keys(border).length ? { border } : undefined;
            setCellValueWithFont(worksheet, rowNumber, cfg.col, cfg.val(co) as any, style as any);
        });
        rowNumber++;
    });

    return rowNumber + 1;
};

const addLaytimeTextRows = (worksheet: ExcelJS.Worksheet, rowNumber: number): number => {
    rowNumber++;

    setCellValueWithFont(
        worksheet,
        rowNumber,
        1,
        `Laytime to commence in accordance with terms and conditions of the above mentioned\nCharter Party. This Notice of Readiness is tendered without prejudice to any previous Notice\nof Readiness tendered by or on behalf of the vessel.\nPlease acknowledge receipt of this Notice of Readiness by signing and returning duplicate\ncopies herewith.`,
        {
            mergeAcross: 9,
            mergeDown: 5,
            align: 'left',
            wrapText: true,
        }
    );

    return rowNumber + 6;
};

const setDefaultColumnWidths = (workheet: ExcelJS.Worksheet) => {
    workheet.getColumn(1).width = 9;
    workheet.getColumn(2).width = 2;
    workheet.getColumn(3).width = 14;
    workheet.getColumn(4).width = 21;
    workheet.getColumn(5).width = 9;
    workheet.getColumn(6).width = 17;
    workheet.getColumn(7).width = 9;
    workheet.getColumn(8).width = 14;
    workheet.getColumn(9).width = 7;
};

export const generateNorTenderReport = (
    workbook: ExcelJS.Workbook,
    cargoEventReportData: CargoEventReportData,
    logoImageId: number,
    eventInputId: EventInputEnum
): void => {
    const _worksheet = workbook.addWorksheet('NOR Tender');
    setupWorksheetForPrint(_worksheet);
    // Hide gridlines and keep a clean white background
    _worksheet.views = [{ state: 'normal', showGridLines: false }];
    const portCall = cargoEventReportData.selectedPortCall;
    const selectedEvent = cargoEventReportData.tankersEvents.find((ev) => !ev.deleted && ev.eventId === cargoEventReportData.excelReportInfo.eventId);
    const cargoOperations = getCargoOperations(
        portCall.berthCalls.flatMap((berthCall) => berthCall.cargoOperations),
        selectedEvent,
        eventInputId
    );
    if (cargoOperations.length === 0 || !selectedEvent) return;
    setDefaultColumnWidths(_worksheet);
    addReportHeader(_worksheet, logoImageId, cargoOperations[0]);
    addVesselDetails(
        _worksheet,
        8,
        1,
        7,
        portCall.voyageNumber,
        portCall.portDisplayName,
        cargoEventReportData.vessel,
        null,
        selectedEvent.eventDate.actualDateTimeLocal
    );
    addPortDescription(_worksheet, selectedEvent, portCall, cargoOperations);
    let rowNumber = addCargoOperationRows(_worksheet, cargoOperations);
    rowNumber = addLaytimeTextRows(_worksheet, rowNumber);
    const endRow = addChartererAndDateTime(
        _worksheet,
        rowNumber + 3,
        cargoOperations.find((co) => co.cpDate),
        selectedEvent,
        'Master'
    );
    setWorksheetPrintArea(_worksheet, endRow, 9);
};
